import React from 'react';

interface RegionCost {
  region: string;
  cost: number;
  percentage: number;
}

interface CostByRegionProps {
  regions: RegionCost[];
  currency?: string;
}

/**
 * Cost by Region Component
 * Displays cost breakdown by AWS/Azure/GCP regions
 */
export const CostByRegion: React.FC<CostByRegionProps> = ({ regions, currency = 'USD' }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (!regions || regions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
        <h3 className="text-lg font-semibold text-foreground">Cost by Region</h3>
        <p className="mt-4 text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  // Sort by cost descending
  const sortedRegions = [...regions].sort((a, b) => b.cost - a.cost);
  const topRegions = sortedRegions.slice(0, 10); // Show top 10

  return (
    <div className="rounded-lg border border-border bg-card p-6 dark:border-border dark:bg-card">
      <h3 className="text-lg font-semibold text-foreground">Cost by Region</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Top {topRegions.length} regions by cost
      </p>

      <div className="mt-6 space-y-4">
        {topRegions.map((region, index) => (
          <div key={region.region} className="flex items-center gap-4">
            {/* Rank */}
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary dark:bg-primary/10 dark:text-primary">
              {index + 1}
            </div>

            {/* Region Name */}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-foreground">
                {region.region}
              </div>
              {/* Progress Bar */}
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${region.percentage}%` }}
                />
              </div>
            </div>

            {/* Cost and Percentage */}
            <div className="flex flex-col items-end">
              <div className="text-sm font-semibold text-foreground">
                {formatCurrency(region.cost)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatPercentage(region.percentage)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedRegions.length > 10 && (
        <div className="mt-4 text-center text-sm text-muted-foreground">
          +{sortedRegions.length - 10} more regions
        </div>
      )}
    </div>
  );
};
